import { supabase, getWebSocketState } from '@/lib/supabase';
import { RealtimeChannel, RealtimePostgresChangesPayload, REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';

/**
 * Centralized real-time subscription service for Supabase
 * Manages all real-time channels and provides type-safe handlers
 * Includes WebSocket error handling and automatic reconnection
 */

// Type definitions for real-time payloads
interface TradingSignalPayload {
  id: string;
  asset_class: string;
  asset_symbol: string;
  signal_name: string;
  direction: 'BUY' | 'SELL' | 'HOLD';
  entry_price: number;
  target_price: number;
  stop_loss: number;
  confidence_score: number;
  timeframe: string;
  is_active: boolean;
  created_at: string;
  expires_at?: string;
  description?: string;
}

interface PortfolioHoldingPayload {
  id: string;
  user_id: string;
  asset_class: string;
  asset_symbol: string;
  quantity: number;
  average_cost_per_unit: number;
  current_market_price: number;
  position_status: 'OPEN' | 'CLOSED' | 'PARTIAL';
  opened_at: string;
  last_updated: string;
  notes?: string;
}

interface CommunityActivityPayload {
  id: string;
  user_id: string;
  activity_type: 'win' | 'achievement' | 'milestone' | 'streak';
  activity_id: string;
  reaction_type?: 'like' | 'celebrate' | 'fire' | 'rocket' | 'clap';
  created_at: string;
}

interface MessagePayload {
  id: string;
  conversation_id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  status: 'sent' | 'delivered' | 'read';
  created_at: string;
}

// Handler types
type SignalHandler = (signal: TradingSignalPayload, event: 'INSERT' | 'UPDATE' | 'DELETE') => void;
type PortfolioHandler = (holding: PortfolioHoldingPayload, event: 'INSERT' | 'UPDATE' | 'DELETE') => void;
type CommunityHandler = (activity: CommunityActivityPayload, event: 'INSERT' | 'UPDATE' | 'DELETE') => void;
type MessageHandler = (message: MessagePayload, event: 'INSERT' | 'UPDATE' | 'DELETE') => void;
type ErrorHandler = (error: Error, channelName: string) => void;
type ConnectionStatusHandler = (status: 'connected' | 'disconnected' | 'connecting' | 'error', channelName: string) => void;

interface SubscriptionOptions {
  onError?: ErrorHandler;
  onConnectionStatus?: ConnectionStatusHandler;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
}

class RealtimeService {
  private channels: Map<string, RealtimeChannel> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000; // Start with 2 seconds
  private connectionHealthCheckInterval?: NodeJS.Timeout;

  constructor() {
    // Start connection health monitoring
    this.startConnectionHealthCheck();
  }

  /**
   * Monitor overall WebSocket connection health
   */
  private startConnectionHealthCheck(): void {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // Check connection health every 30 seconds
    this.connectionHealthCheckInterval = setInterval(() => {
      const wsState = getWebSocketState();
      const activeChannels = this.channels.size;

      console.log('[Realtime] Connection health check:', {
        wsState,
        activeChannels,
        timestamp: new Date().toISOString(),
      });

      // If WebSocket is in error state and we have active channels, attempt recovery
      if (wsState === 'error' && activeChannels > 0) {
        console.warn('[Realtime] WebSocket in error state, attempting to recover channels');
        this.reconnectAll();
      }
    }, 30000);
  }

  /**
   * Enhanced channel subscription with error handling and reconnection
   */
  private subscribeChannel(
    channelName: string,
    options?: SubscriptionOptions
  ): RealtimeChannel {
    const existingChannel = this.channels.get(channelName);
    
    if (existingChannel) {
      return existingChannel;
    }

    const channel = supabase.channel(channelName, {
      config: {
        broadcast: { self: false },
        presence: { key: '' },
      },
    });

    // Handle subscription status changes
    channel.on('system', { event: '*' }, (payload) => {
      const status = payload.status;
      
      if (options?.onConnectionStatus) {
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
          options.onConnectionStatus('connected', channelName);
          this.reconnectAttempts.set(channelName, 0); // Reset attempts on success
        } else if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
          options.onConnectionStatus('error', channelName);
          this.handleChannelError(channelName, new Error('Channel subscription error'), options);
        } else if (status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT) {
          options.onConnectionStatus('error', channelName);
          this.handleChannelError(channelName, new Error('Channel subscription timed out'), options);
        } else if (status === REALTIME_SUBSCRIBE_STATES.CLOSED) {
          options.onConnectionStatus('disconnected', channelName);
        }
      }

      // Log status for debugging
      console.log(`[Realtime] Channel ${channelName} status:`, status, {
        wsState: getWebSocketState(),
        timestamp: new Date().toISOString(),
      });
    });

    this.channels.set(channelName, channel);
    return channel;
  }

  /**
   * Handle channel errors with automatic reconnection
   */
  private handleChannelError(
    channelName: string,
    error: Error,
    options?: SubscriptionOptions
  ): void {
    const wsState = getWebSocketState();
    
    console.error(`[Realtime] Error in channel ${channelName}:`, {
      error: error.message,
      wsState,
      timestamp: new Date().toISOString(),
    });

    if (options?.onError) {
      options.onError(error, channelName);
    }

    // Attempt reconnection if enabled
    if (options?.autoReconnect !== false) {
      const attempts = this.reconnectAttempts.get(channelName) || 0;
      const maxAttempts = options?.maxReconnectAttempts || this.maxReconnectAttempts;

      if (attempts < maxAttempts) {
        this.reconnectAttempts.set(channelName, attempts + 1);
        const delay = this.reconnectDelay * Math.pow(2, attempts); // Exponential backoff

        console.log(`[Realtime] Reconnecting channel ${channelName} in ${delay}ms (attempt ${attempts + 1}/${maxAttempts})`);

        setTimeout(() => {
          const channel = this.channels.get(channelName);
          if (channel) {
            channel.subscribe((status) => {
              if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
                console.log(`[Realtime] Successfully reconnected channel ${channelName}`);
              }
            });
          }
        }, delay);
      } else {
        console.error(`[Realtime] Max reconnection attempts reached for channel ${channelName}`);
        if (options?.onError) {
          options.onError(new Error('Max reconnection attempts reached'), channelName);
        }
        
        // Emit event for UI to handle
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('realtime-max-retries', {
            detail: {
              channelName,
              maxAttempts,
            },
          }));
        }
      }
    }
  }

  /**
   * Subscribe to live trading signals updates with enhanced error handling
   */
  subscribeToSignals(
    handler: SignalHandler,
    filters?: {
      assetClass?: string;
      userId?: string;
      isActive?: boolean;
    },
    options?: SubscriptionOptions
  ): () => void {
    const channelName = 'trading-signals';
    
    const channel = this.subscribeChannel(channelName, options);

    // Build filter string for Supabase
    let filterString = '';
    if (filters?.assetClass) {
      filterString += `asset_class=eq.${filters.assetClass}`;
    }
    if (filters?.userId) {
      filterString += filterString ? ',' : '';
      filterString += `created_by=eq.${filters.userId}`;
    }
    if (filters?.isActive !== undefined) {
      filterString += filterString ? ',' : '';
      filterString += `is_active=eq.${filters.isActive}`;
    }

    try {
      channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'trading_signals',
            filter: filterString || undefined,
          },
          (payload: RealtimePostgresChangesPayload<TradingSignalPayload>) => {
            try {
              const eventType = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
              const data = (payload.new && Object.keys(payload.new).length > 0 ? payload.new : payload.old) as TradingSignalPayload | undefined;
              if (data && Object.keys(data).length > 0) {
                handler(data, eventType);
              }
            } catch (error) {
              console.error('[Realtime] Error in signal handler:', error);
              if (options?.onError) {
                options.onError(error as Error, channelName);
              }
            }
          }
        )
        .subscribe((status) => {
          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            console.log(`[Realtime] Subscribed to ${channelName}`);
          } else if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
            this.handleChannelError(channelName, new Error('Subscription failed'), options);
          }
        });
    } catch (error) {
      this.handleChannelError(channelName, error as Error, options);
    }

    // Return unsubscribe function
    return () => this.unsubscribeChannel(channelName);
  }

  /**
   * Subscribe to portfolio holdings updates with enhanced error handling
   */
  subscribeToPortfolio(
    userId: string,
    handler: PortfolioHandler,
    options?: SubscriptionOptions
  ): () => void {
    const channelName = `portfolio-${userId}`;
    
    const channel = this.subscribeChannel(channelName, options);

    try {
      channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'portfolio_holdings',
            filter: `user_id=eq.${userId}`,
          },
          (payload: RealtimePostgresChangesPayload<PortfolioHoldingPayload>) => {
            try {
              const eventType = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
              const data = (payload.new && Object.keys(payload.new).length > 0 ? payload.new : payload.old) as PortfolioHoldingPayload | undefined;
              if (data && Object.keys(data).length > 0) {
                handler(data, eventType);
              }
            } catch (error) {
              console.error('[Realtime] Error in portfolio handler:', error);
              if (options?.onError) {
                options.onError(error as Error, channelName);
              }
            }
          }
        )
        .subscribe((status) => {
          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            console.log(`[Realtime] Subscribed to ${channelName}`);
          } else if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
            this.handleChannelError(channelName, new Error('Subscription failed'), options);
          }
        });
    } catch (error) {
      this.handleChannelError(channelName, error as Error, options);
    }

    return () => this.unsubscribeChannel(channelName);
  }

  /**
   * Subscribe to community activity feed with enhanced error handling
   */
  subscribeToCommunityActivity(
    handler: CommunityHandler,
    options?: SubscriptionOptions
  ): () => void {
    const channelName = 'community-activity';
    
    const channel = this.subscribeChannel(channelName, options);

    try {
      channel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'community_activity_reactions',
          },
          (payload: RealtimePostgresChangesPayload<CommunityActivityPayload>) => {
            try {
              const data = payload.new as CommunityActivityPayload | undefined;
              if (data && Object.keys(data).length > 0) {
                handler(data, 'INSERT');
              }
            } catch (error) {
              console.error('[Realtime] Error in community handler:', error);
              if (options?.onError) {
                options.onError(error as Error, channelName);
              }
            }
          }
        )
        .subscribe((status) => {
          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            console.log(`[Realtime] Subscribed to ${channelName}`);
          } else if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
            this.handleChannelError(channelName, new Error('Subscription failed'), options);
          }
        });
    } catch (error) {
      this.handleChannelError(channelName, error as Error, options);
    }

    return () => this.unsubscribeChannel(channelName);
  }

  /**
   * Subscribe to messages with enhanced error handling
   */
  subscribeToMessages(
    conversationId: string,
    handler: MessageHandler,
    options?: SubscriptionOptions
  ): () => void {
    const channelName = `messages-${conversationId}`;
    
    const channel = this.subscribeChannel(channelName, options);

    try {
      channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`,
          },
          (payload: RealtimePostgresChangesPayload<MessagePayload>) => {
            try {
              const eventType = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
              const data = (payload.new && Object.keys(payload.new).length > 0 ? payload.new : payload.old) as MessagePayload | undefined;
              if (data && Object.keys(data).length > 0) {
                handler(data, eventType);
              }
            } catch (error) {
              console.error('[Realtime] Error in message handler:', error);
              if (options?.onError) {
                options.onError(error as Error, channelName);
              }
            }
          }
        )
        .subscribe((status) => {
          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            console.log(`[Realtime] Subscribed to ${channelName}`);
          } else if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
            this.handleChannelError(channelName, new Error('Subscription failed'), options);
          }
        });
    } catch (error) {
      this.handleChannelError(channelName, error as Error, options);
    }

    return () => this.unsubscribeChannel(channelName);
  }

  /**
   * Subscribe to user achievements with enhanced error handling
   */
  subscribeToUserAchievements(
    userId: string,
    handler: (achievement: any, event: 'INSERT') => void,
    options?: SubscriptionOptions
  ): () => void {
    const channelName = `achievements-${userId}`;
    
    const channel = this.subscribeChannel(channelName, options);

    try {
      channel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'user_achievements',
            filter: `user_id=eq.${userId}`,
          },
          (payload: RealtimePostgresChangesPayload<any>) => {
            try {
              if (payload.new) {
                handler(payload.new, 'INSERT');
              }
            } catch (error) {
              console.error('[Realtime] Error in achievements handler:', error);
              if (options?.onError) {
                options.onError(error as Error, channelName);
              }
            }
          }
        )
        .subscribe((status) => {
          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            console.log(`[Realtime] Subscribed to ${channelName}`);
          } else if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
            this.handleChannelError(channelName, new Error('Subscription failed'), options);
          }
        });
    } catch (error) {
      this.handleChannelError(channelName, error as Error, options);
    }

    return () => this.unsubscribeChannel(channelName);
  }

  /**
   * Unsubscribe from a specific channel
   */
  private unsubscribeChannel(channelName: string): void {
    const channel = this.channels.get(channelName);
    if (channel) {
      try {
        supabase.removeChannel(channel);
        this.channels.delete(channelName);
        this.reconnectAttempts.delete(channelName);
        console.log(`[Realtime] Unsubscribed from ${channelName}`);
      } catch (error) {
        console.error(`[Realtime] Error unsubscribing from ${channelName}:`, error);
      }
    }
  }

  /**
   * Unsubscribe from all active channels
   */
  unsubscribeAll(): void {
    console.log(`[Realtime] Unsubscribing from ${this.channels.size} channels`);
    this.channels.forEach((channel, channelName) => {
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        console.error(`[Realtime] Error removing channel ${channelName}:`, error);
      }
    });
    this.channels.clear();
    this.reconnectAttempts.clear();
    
    // Stop health check
    if (this.connectionHealthCheckInterval) {
      clearInterval(this.connectionHealthCheckInterval);
      this.connectionHealthCheckInterval = undefined;
    }
  }

  /**
   * Get the count of active subscriptions
   */
  getActiveSubscriptionsCount(): number {
    return this.channels.size;
  }

  /**
   * Get connection status for all channels
   */
  getChannelStatuses(): Map<string, string> {
    const statuses = new Map<string, string>();
    this.channels.forEach((channel, name) => {
      statuses.set(name, channel.state);
    });
    return statuses;
  }

  /**
   * Manually reconnect a specific channel
   */
  reconnectChannel(channelName: string): void {
    const channel = this.channels.get(channelName);
    if (channel) {
      console.log(`[Realtime] Manually reconnecting channel ${channelName}`);
      channel.subscribe();
    } else {
      console.warn(`[Realtime] Channel ${channelName} not found for reconnection`);
    }
  }

  /**
   * Manually reconnect all channels
   */
  reconnectAll(): void {
    console.log(`[Realtime] Manually reconnecting ${this.channels.size} channels`);
    this.channels.forEach((channel, channelName) => {
      channel.subscribe();
    });
  }

  /**
   * Get comprehensive connection diagnostics
   */
  getConnectionDiagnostics(): {
    activeChannels: number;
    wsState: string;
    channelStatuses: Map<string, string>;
    reconnectAttempts: Map<string, number>;
  } {
    return {
      activeChannels: this.channels.size,
      wsState: getWebSocketState(),
      channelStatuses: this.getChannelStatuses(),
      reconnectAttempts: new Map(this.reconnectAttempts),
    };
  }
}

// Export singleton instance
export const realtimeService = new RealtimeService();